import classNames from 'clsx';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { styles as sharedStyles } from '@swf/account';
import createForm from '@swf/components/Forms/Form';
import StarsDropDown from '@swf/components/Inputs/StarsDropDown';
import Text from '@swf/components/Inputs/Text';
import ResolverLink from '@swf/components/ResolverLink/ResolverLink';
import StarsButton from '@swf/components/StarsButton/StarsButton';
import StarsInfo from '@swf/components/StarsInfo/StarsInfo';
import Translation from '@swf/components/Translation/Translation';
import { FeatureFlag, ResolverTag } from '@swf/core/enums';
import useFeature from '@swf/core/hooks/useFeature';
import type { Dispatch } from '@swf/core/store';
import { select } from '@swf/core/store';
import type { TrackedTranslation } from '@swf/core/types/i18n';
import { scrollToTop } from '@swf/core/utils';
import { buildFormErrors } from '@swf/core/utils/forms';
import {
  getValidatorsForLicense,
  minLength,
  mustNotContainSymbols,
  required
} from '@swf/core/utils/validators';
import countries from '@swf/generated/countries.json';
import states from '@swf/generated/states.json';

import {
  TXTCLI_Address,
  TXTCLI_Apply__forChanges,
  TXTCLI_City,
  TXTCLI_City_must_be_at_least_2_charac_ELL,
  TXTCLI_Country,
  TXTCLI_Please_enter_ZIP___Postal_Code,
  TXTCLI_State___Province,
  TXTCLI_Street_address_must_be_at_leas_ELL,
  TXTCLI_The_street_address_entered_app_ELL,
  TXTCLI_Wrong_Zip_format,
  TXTCLI_Zip_Post_Code,
  TXTWCL_Please_contact_Xa_hrefXXcontac_ELL_2,
  TXTWCL_The_city_you_have_entered_is_i_ELL_1,
  TXTWCL_Your_changes_have_been_saved_s_ELL
} from './AddressForm.messages.json';
import styles from './AddressForm.scss';
import StreetAddressExtended from './StreetAddressExtended';

interface Props {
  className?: string;
  submitButtonText?: TrackedTranslation;
  successCallback?: () => void;
  hasCountryState?: boolean;
  disabled?: boolean;
}

interface FormValues {
  streetAddress?: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  streetName?: string;
  buildingNumber?: string;
}

const errorTranslations = {
  TXTCLI_Street_address_must_be_at_leas_ELL,
  TXTCLI_The_street_address_entered_app_ELL,
  TXTCLI_Wrong_Zip_format
};

const restrictedCountries = ['AR'];

const restrictedStates = [
  'B', // Buenos Aires
  'C' // CABA
];

const Form = createForm<FormValues>();

const CITY_NOT_ALLOWED_SYMBOLS = /[.\\~`!@#$%^&*()_+={}/?|:;"<>,\][]/;

const AddressForm: React.FC<Props> = ({
  className,
  submitButtonText = TXTCLI_Apply__forChanges,
  hasCountryState = true,
  successCallback,
  disabled
}) => {
  const site = useSelector(select.site.getId);
  const streetAddress = useSelector(select.account.getAddress);
  const buildingNumber = useSelector(select.account.getBuildingNumber);
  const streetName = useSelector(select.account.getStreetName);
  const city = useSelector(select.account.getCity);
  const zipCode = useSelector(select.account.getZipCode);
  const state = useSelector(select.account.getAddressState) as string;
  const country = useSelector(select.account.getCountry) as string;
  const userId = useSelector(select.account.getUserId);
  const email = useSelector(select.account.getEmail);
  const locale = useSelector(select.locale.get);
  const disableBuenosAiresFlag = useFeature(FeatureFlag.DisableBuenosAiresFeatures);
  const euDeOasisStreetAddress = useFeature(FeatureFlag.EuDeOasisStreetAddress);

  const {
    account: { updateAddress }
  } = useDispatch<Dispatch>();

  const onSubmit = async (values: FormValues) => {
    const response = await updateAddress({
      ...values,
      country,
      streetAddress: euDeOasisStreetAddress
        ? `${values.streetName} ${values.buildingNumber}`
        : values.streetAddress
    });
    scrollToTop();
    const errors = buildFormErrors(response, errorTranslations);
    if (successCallback && !errors) {
      successCallback();
    }

    return errors;
  };

  /* If in Buenos Aires, we should disable the State Field.
  We can turn this functionality on and off using the `DisableBuenosAiresFeatures` flag.
  If `DisableBuenosAiresFeatures` is true, state field will remain active. */

  const restrictAddressChanges =
    !disableBuenosAiresFlag &&
    restrictedCountries.includes(country) &&
    restrictedStates.includes(state);

  const countryStates = country && states[country];
  const restricted = restrictAddressChanges;

  // TODO: generate .d.ts for countries.json
  const initialCountry = (countries as { value: string; text: string }[]).find(
    ({ value }) => value === country
  )?.text;

  const initialFormValues: FormValues = {
    streetAddress,
    streetName,
    buildingNumber,
    city,
    zip: zipCode,
    state,
    country: initialCountry
  } as FormValues;

  return (
    <Form
      onSubmit={onSubmit}
      initialValues={initialFormValues}
      className={className}
      render={({
        dirtySinceLastSubmit,
        hasSubmitErrors,
        pristine,
        submitError,
        submitSucceeded,
        submitting
      }) => (
        <>
          {restricted && (
            <Translation
              values={{
                a: (msg: string) => (
                  <ResolverLink
                    resolverTag={ResolverTag.AddressChangeSupport}
                    query={{ language: locale, userId, email }}
                  >
                    {msg}
                  </ResolverLink>
                )
              }}
              {...TXTWCL_Please_contact_Xa_hrefXXcontac_ELL_2}
            />
          )}
          {submitSucceeded && (
            <StarsInfo type="confirmation" className={styles.message}>
              <Translation {...TXTWCL_Your_changes_have_been_saved_s_ELL} />
            </StarsInfo>
          )}
          {submitError && (
            <StarsInfo type="error" className={styles.message}>
              {submitError}
            </StarsInfo>
          )}
          {euDeOasisStreetAddress ? (
            <StreetAddressExtended restricted={restricted} disabled={disabled} />
          ) : (
            <Text
              id="streetAddress"
              label={TXTCLI_Address}
              disabled={restricted || disabled}
              validators={[
                minLength(4, TXTCLI_Street_address_must_be_at_leas_ELL),
                ...getValidatorsForLicense(site, 'address')
              ]}
            />
          )}
          {!euDeOasisStreetAddress && (
            <Text
              id="city"
              label={TXTCLI_City}
              disabled={restricted || disabled}
              validators={[
                required(),
                minLength(2, TXTCLI_City_must_be_at_least_2_charac_ELL),
                mustNotContainSymbols(
                  CITY_NOT_ALLOWED_SYMBOLS,
                  TXTWCL_The_city_you_have_entered_is_i_ELL_1
                )
              ]}
            />
          )}
          <Text
            id="zip"
            label={TXTCLI_Zip_Post_Code}
            disabled={restricted || disabled}
            validators={[required(TXTCLI_Please_enter_ZIP___Postal_Code)]}
          />
          {euDeOasisStreetAddress && (
            <Text
              id="city"
              label={TXTCLI_City}
              disabled={restricted || disabled}
              validators={[
                required(),
                minLength(2, TXTCLI_City_must_be_at_least_2_charac_ELL),
                mustNotContainSymbols(
                  CITY_NOT_ALLOWED_SYMBOLS,
                  TXTWCL_The_city_you_have_entered_is_i_ELL_1
                )
              ]}
            />
          )}
          {hasCountryState && countryStates && (
            <StarsDropDown
              id="state"
              label={TXTCLI_State___Province}
              options={countryStates}
              disabled={restricted || disabled}
              validators={[required()]}
            />
          )}
          {hasCountryState && <Text id="country" label={TXTCLI_Country} disabled />}
          <StarsButton
            block
            loading={submitting}
            disabled={pristine || (hasSubmitErrors && !dirtySinceLastSubmit)}
            type="submit"
            className={classNames(sharedStyles.submit)}
            translation={submitButtonText}
          />
        </>
      )}
    />
  );
};

export default AddressForm;
